import type {
  CommentNode,
  FeedSort,
  Page,
  PromptCard,
  PromptDetail,
  PromptType,
} from '@promptvault/shared';
import { apiFetch } from './client';

/**
 * Re-exported, not redeclared.
 *
 * This was a second hand-written copy of the sort union, and it silently fell a value behind the
 * server the moment one was added — the same class of drift that let the whole engagement
 * contract go missing. One definition, in shared, imported everywhere.
 */
export type { FeedSort };

export interface FeedParams {
  type?: PromptType;
  model?: string;
  category?: string;
  tag?: string;
  q?: string;
  sort?: FeedSort;
  /** Canonical platform key — `civitai.com`, `github.com`. */
  platform?: string;
  nsfw?: boolean;
  cursor?: string;
  limit?: number;
}

export function fetchFeed(params: FeedParams): Promise<Page<PromptCard>> {
  return apiFetch<Page<PromptCard>>('/feed', {
    params: params as Record<string, string | number | boolean | undefined>,
  });
}

/**
 * What `/search` answers when it had to fix a spelling to find anything.
 *
 * This was missing from the declared return type, which is why nothing ever mismatched: the
 * server has reported `correction` since the fuzzy fallback shipped, the client typed the
 * response as a plain `Page<PromptCard>`, and the field was dropped at the boundary. So a query
 * for `cyberpnk` silently became a query for `cyberpunk` and the user was never told — the exact
 * behaviour D64 says must never happen. A contract can be wrong by omission and still typecheck
 * everywhere (D47-second).
 */
export interface SearchCorrection {
  original: string;
  corrected: string;
  terms: { from: string; to: string }[];
}

export type SearchPage = Page<PromptCard> & { correction?: SearchCorrection };

export function fetchSearch(q: string, cursor?: string, exact = false): Promise<SearchPage> {
  return apiFetch<SearchPage>('/search', {
    params: { q, cursor, ...(exact ? { exact: 'true' } : {}) },
  });
}

export function fetchPrompt(id: string): Promise<PromptDetail> {
  return apiFetch<PromptDetail>(`/prompts/${id}`);
}

export function fetchComments(promptId: string, cursor?: string): Promise<Page<CommentNode>> {
  return apiFetch<Page<CommentNode>>(`/prompts/${promptId}/comments`, { params: { cursor } });
}

export function ratePrompt(
  promptId: string,
  stars: number,
): Promise<{ ratingAvg: number; ratingCount: number; myRating: number }> {
  return apiFetch(`/prompts/${promptId}/rate`, {
    method: 'POST',
    body: JSON.stringify({ stars }),
  });
}

export function downloadPrompt(promptId: string): Promise<{
  title: string;
  body: string;
  instructions: string | null;
  negativePrompt: string | null;
}> {
  return apiFetch(`/prompts/${promptId}/download`, { method: 'POST' });
}

export function toggleFavorite(
  promptId: string,
): Promise<{ favorited: boolean; favoriteCount: number }> {
  return apiFetch(`/prompts/${promptId}/favorite`, { method: 'POST' });
}

export function postComment(
  promptId: string,
  body: string,
  parentId?: string,
): Promise<CommentNode> {
  return apiFetch(`/prompts/${promptId}/comments`, {
    method: 'POST',
    body: JSON.stringify({ body, parentId }),
  });
}

export function likeComment(commentId: string): Promise<{ liked: boolean; likeCount: number }> {
  return apiFetch(`/comments/${commentId}/like`, { method: 'POST' });
}
